import { map } from "rxjs/operators";
import { combineLatest, Observable, of, switchMap } from "rxjs";
import {
	CashPlusApiCombined,
	CashPlusPortfolioApi,
	FiatWalletsConfigurations
} from "@modules/cashplus/entities/cashplus.model";
import { Injectable } from "@angular/core";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";

@Injectable({ providedIn: "root" })
export class FiatWalletTransactionFacade {
	public constructor(
		private readonly cashPlusPortfolioFacade: CashplusPortfolioFacade,
		private readonly cashPlusFacade: CashPlusFacade,
		private readonly launchDarklyService: LaunchDarklyService
	) {}

	public getFiatConfigByFiatId$(fiatId: string): Observable<FiatWalletsConfigurations> {
		return this.cashPlusFacade
			.getConfigurationsState$()
			.pipe(map((config) => config.configurations.find((el) => el.fiat_id === fiatId)));
	}

	public getCashPlusPortfolioState$(fiatId: string): Observable<CashPlusPortfolioApi> {
		let fiatPid: string = "";
		return this.cashPlusFacade.getConfigurationsPerFiatState$(fiatId).pipe(
			switchMap((config) => {
				if (config) {
					fiatPid = config.fiat_pid;
					return this.cashPlusPortfolioFacade.getPortfolioPerFiatState$(fiatPid);
				}
				return of(null);
			})
		);
	}

	public fiatWalletViewModel$(fiatId: string): Observable<FiatWalletCashPlusInterface> {
		return combineLatest([
			this.cashPlusFacade.isCashPlusEnabled$(),
			this.cashPlusFacade.getSubscriptionsState$(),
			this.cashPlusFacade.getConfigurationsState$(),
			this.getFiatConfigByFiatId$(fiatId),
			this.getCashPlusPortfolioState$(fiatId),
			this.cashPlusPortfolioFacade.getPortfolioState$(),
			this.cashPlusFacade.isFiatIdEligibleCashPlus$(fiatId),
			this.launchDarklyService.isActive$(LD_FLAG.ADVANCED_TRADING)
		]).pipe(
			map(
				([
					cashPlusEnabled,
					subscriptions,
					configurations,
					configFiatPid,
					portfolio,
					portfolioState,
					fiatEligible,
					isAdvancedTradingEnabled
				]) => ({
					isCashPlusEnabled: cashPlusEnabled,
					subsState: subscriptions,
					configState: configurations,
					configPidState: configFiatPid,
					portfolioState: portfolio,
					isPortfolioLoaded: !portfolioState.error,
					isSubscriptionLoaded: !subscriptions.error,
					isConfigurationLoaded: !configurations.error,
					isFiatEligible: fiatEligible,
					isAdvancedTradingEnabled
				})
			)
		);
	}
}

export interface FiatWalletCashPlusInterface extends CashPlusApiCombined {
	isCashPlusEnabled: boolean;
	isPortfolioLoaded: boolean;
	isSubscriptionLoaded: boolean;
	isConfigurationLoaded: boolean;
	isFiatEligible: boolean;
	isAdvancedTradingEnabled: boolean;
}
